import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthCard, Input, Button } from '../../components';
import api from '../../utils/api';
import './ForgotPassword.scss';

/**
 * Forgot Password page component
 * Handles password reset requests
 */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (!email) {
      setError('Email is required');
      setIsLoading(false);
      return;
    }

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthCard
        title="Check Your Email"
        subtitle="We've sent password reset instructions"
      >
        <div className="success-message">
          <p>
            If an account exists for <strong>{email}</strong>, you will receive an email with
            instructions to reset your password.
          </p>
          <p className="help-text">
            Didn't receive the email? Check your spam folder or try again.
          </p>
        </div>

        <Link to="/login" className="back-to-login">
          <Button variant="ghost" fullWidth>
            Back to Login
          </Button>
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Forgot Password?"
      subtitle="Enter your email to reset your password"
      footerText="Remember your password?"
      footerLink={{ text: 'Log in', to: '/login' }}
    >
      <form onSubmit={handleSubmit} className="forgot-password-form">
        {error && <div className="error-banner">{error}</div>}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          autoComplete="email"
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          isLoading={isLoading}
          disabled={!email}
        >
          {isLoading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
    </AuthCard>
  );
};

export default ForgotPassword;
